import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import {
  useQueryParam,
  StringParam,
  NumberParam,
  withDefault
} from "use-query-params";

import { Container } from "@material-ui/core";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import { useAdmin } from "src/components/LoggedInAdminProvider";
import PageTitle from "src/components/PageTitle";
import { useSubaccount } from "src/components/SubaccountProvider";
import {
  adminChangeRole,
  adminChangeRoleVariables
} from "src/graphql/generated/adminChangeRole";
import {
  adminDelete,
  adminDeleteVariables
} from "src/graphql/generated/adminDelete";
import {
  adminInvite,
  adminInviteVariables
} from "src/graphql/generated/adminInvite";
import {
  adminUndelete,
  adminUndeleteVariables
} from "src/graphql/generated/adminUndelete";
import { Role } from "src/graphql/generated/globalTypes";
import {
  listSubaccountMemberships,
  listSubaccountMembershipsVariables
} from "src/graphql/generated/listSubaccountMemberships";
import useMutation from "src/util/useMutation";

import AdminListControls from "./AdminListControls";
import AdminTable from "./AdminTable";
import ChangeRoleDialog from "./ChangeRoleDialog";
import InviteDialog, { Values as InviteDialogValues } from "./InviteDialog";
import UndeleteDialog from "./UndeleteDialog";
import {
  AdminSort,
  AdminStatus,
  ADMIN_SORTS,
  ADMIN_STATUSES
} from "./constants";
import {
  CHANGE_ADMIN_ROLE,
  DELETE_ADMIN,
  GET_ADMINS,
  INVITE_ADMIN,
  UNDELETE_ADMIN
} from "./queries";
import { Admin, InviteResult, codeIsInviteError } from "./types";

function validateSort(sort: string): AdminSort {
  return ADMIN_SORTS.find(s => s.key === sort)?.key ?? ADMIN_SORTS[0].key;
}

function validateStatus(status: string): AdminStatus {
  return (
    ADMIN_STATUSES.find(s => s.key === status)?.key ?? ADMIN_STATUSES[0].key
  );
}

export default function AdminList(): JSX.Element {
  const { subaccount } = useSubaccount()!;
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [status, setStatus] = useQueryParam(
    "status",
    withDefault(StringParam, ADMIN_STATUSES[0].key)
  );
  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, ADMIN_SORTS[0].key)
  );

  const validatedStatus = validateStatus(status);
  const validatedSort = validateSort(sort);

  const { loading, error, data, refetch } = useQuery<
    listSubaccountMemberships,
    listSubaccountMembershipsVariables
  >(GET_ADMINS, {
    pollInterval: 60 * 1000,
    variables: { subaccountId: subaccount.id }
  });

  const [deleteAdmin] = useMutation<adminDelete, adminDeleteVariables>(
    DELETE_ADMIN,
    {
      onErrorSnackbarMessage: "Could not deactivate admin"
    }
  );

  const [undeleteAdmin] = useMutation<adminUndelete, adminUndeleteVariables>(
    UNDELETE_ADMIN
  );

  const [inviteAdmin] = useMutation<adminInvite, adminInviteVariables>(
    INVITE_ADMIN
  );

  const [changeAdminRole] = useMutation<
    adminChangeRole,
    adminChangeRoleVariables
  >(CHANGE_ADMIN_ROLE);

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [undeleteDialogOpenForAdmin, setUndeleteDialogOpenForAdmin] =
    useState<Admin | null>(null);
  const [changeRoleDialogOpenForAdmin, setChangeRoleDialogOpenForAdmin] =
    useState<Admin | null>(null);

  const currentUser = useAdmin();
  const currentUserIsStaff = currentUser?.isStaff;

  const allAdmins: Admin[] = useMemo(() => {
    if (!data?.listSubaccountMemberships) {
      return [];
    }

    // Add formatted invited-by data
    return data.listSubaccountMemberships
      .map(membership => ({
        ...membership.admin,
        deletedAt: membership.deletedAt,
        membership
      }))
      .filter(admin => {
        if (admin.isStaff && !currentUserIsStaff) {
          // hide staff accounts from non-staff
          return false;
        }
        return true;
      })
      .map(admin => ({
        ...admin,
        invitedAtStr: new Date(admin.createdAt).toLocaleString(),
        invitedByStr: admin.createdBy
          ? `By: ${
              admin.createdBy.isStaff
                ? "Scale To Win Staff"
                : admin.createdBy.fullName
            }`
          : ""
      }));
  }, [data, currentUserIsStaff]);

  const filteredAdmins: Admin[] = useMemo(() => {
    let filtered = allAdmins.filter(admin => {
      // status filter
      switch (validatedStatus) {
        case "active":
          return !admin.deletedAt && admin.hasLoggedIn;
        case "invited":
          return !admin.hasLoggedIn;
        case "deactivated":
          return !!admin.deletedAt;
      }
    });

    if (search) {
      const searchLower = search.trim().toLocaleLowerCase();
      filtered = filtered.filter(admin => {
        // search
        return (
          (admin.fullName || "").toLocaleLowerCase().includes(searchLower) ||
          (admin.email || "").toLocaleLowerCase().includes(searchLower)
        );
      });
    }

    // sort
    return filtered.sort((a, b) => {
      switch (validatedSort) {
        case "created_at_asc":
          return a.createdAt - b.createdAt;
        case "created_at_desc":
          return b.createdAt - a.createdAt;
        case "name_asc":
          return (a.fullName || "").localeCompare(b.fullName || "");
        case "name_desc":
          return (b.fullName || "").localeCompare(a.fullName || "");
      }
    });
  }, [allAdmins, validatedStatus, search, validatedSort]);

  const rows = filteredAdmins.slice(page * pageSize, (page + 1) * pageSize);

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  async function handleDelete(adminId: string) {
    await deleteAdmin({
      variables: { id: adminId, subaccountId: subaccount.id }
    });
    await refetch();
  }

  async function handleUndelete(adminId: string, role: Role) {
    await undeleteAdmin({
      variables: { id: adminId, subaccountId: subaccount.id, role }
    });
    await refetch();
  }

  async function handleChangeRole(adminId: string, role: Role) {
    await changeAdminRole({
      variables: { adminId, subaccountId: subaccount.id, role }
    });
    await refetch();
  }

  async function handleInvite(
    values: InviteDialogValues & { role: Role }
  ): Promise<InviteResult> {
    const { errors } = await inviteAdmin({
      variables: { ...values, subaccountId: subaccount.id }
    });

    if (errors) {
      const errorCode: string | undefined = errors[0]?.extensions?.code;

      if (errorCode && codeIsInviteError(errorCode)) {
        return { ok: false, error: errorCode };
      }

      return { ok: false, error: "UNKNOWN" };
    }

    await refetch();
    return { ok: true };
  }

  return (
    <Container>
      <PageTitle title="Admins" />
      <AdminListControls
        onClickInvite={() => setInviteDialogOpen(true)}
        statusFilter={validatedStatus}
        setStatusFilter={setStatus}
        search={search}
        setSearch={setSearch}
        sort={validatedSort}
        setSort={setSort}
      />
      {loading ? (
        <LoadingIndicator />
      ) : (
        <AdminTable
          status={validatedStatus}
          rows={rows}
          handleChangePage={setPage}
          handleChangeRowsPerPage={newRowsPerPage => {
            setPageSize(newRowsPerPage);
            setPage(0);
          }}
          rowsPerPage={pageSize}
          page={page}
          totalCount={filteredAdmins.length}
          handleDelete={handleDelete}
          handleUndelete={admin => setUndeleteDialogOpenForAdmin(admin)}
          handleChangeRole={admin => setChangeRoleDialogOpenForAdmin(admin)}
        />
      )}
      <InviteDialog
        handleInvite={handleInvite}
        onClose={() => setInviteDialogOpen(false)}
        open={inviteDialogOpen}
      />
      <UndeleteDialog
        handleUndelete={handleUndelete}
        onClose={() => setUndeleteDialogOpenForAdmin(null)}
        admin={undeleteDialogOpenForAdmin}
      />
      <ChangeRoleDialog
        handleChangeRole={handleChangeRole}
        onClose={() => setChangeRoleDialogOpenForAdmin(null)}
        admin={changeRoleDialogOpenForAdmin}
      />
    </Container>
  );
}
